<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte';
  export const layout = AppLayout;
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import * as Table from "$lib/components/ui/table";
  import { Button } from "$lib/components/ui/button";
  import QRCode from '@castlenine/svelte-qrcode';
  import * as Popover from "$lib/components/ui/popover/index.js";
  import { Plus, Edit3, Trash2, FileText, CheckCircle, EyeOff, FileSpreadsheet, ChevronLeft, ChevronRight, Link, Copy, Check, QrCode } from "lucide-svelte";

  let { short_url, stats } = $props();
  const BASE_URL = 'https://fenti.me';
  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.short_urls || {});
</script>

<div class="p-6 space-y-6">
  <!-- 头部信息 -->
  <div class="flex justify-between items-center">
    <div>
      <h1 class="text-2xl font-bold">{t.analytics || '访问数据统计'}: {short_url.code} 
        <Popover.Root>
          <Popover.Trigger class="inline-flex items-center justify-center size-5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors cursor-pointer" title={t.show_qrcode || '显示网站二维码'}>
            <QrCode class="size-4" />
          </Popover.Trigger>
          <Popover.Content class="w-48 p-3 rounded-xl border bg-popover text-popover-foreground shadow-md z-50">
            <div class="flex flex-col items-center gap-2">
              <div class="size-36 bg-white p-1 rounded-lg border border-border flex items-center justify-center">
                <QRCode data="{BASE_URL}/{short_url.code}" isResponsive />
              </div>
              <span class="text-[10px] text-muted-foreground text-center truncate w-full">
                { t.qrcode_title || 'Scan to Visit Site' }
              </span>
            </div>
          </Popover.Content>
        </Popover.Root>
      </h1>
      
      <p class="text-sm text-muted-foreground break-all mt-1">{short_url.target_url}</p>
    </div>
    <Button variant="outline" onclick={() => router.visit('/short_urls')}>
      {t.back_to_list || '返回列表'}
    </Button>
  </div>

  <!-- 概览指标卡片 -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div class="p-4 border rounded-lg bg-card">
      <div class="text-sm font-medium text-muted-foreground">{t.total_clicks || '总点击量'}</div>
      <div class="text-3xl font-bold mt-2">{short_url.clicks_count || 0}</div>
    </div>
    <div class="p-4 border rounded-lg bg-card">
      <div class="text-sm font-medium text-muted-foreground">{t.region || '节点区域'}</div>
      <div class="text-3xl font-bold mt-2 uppercase">{short_url.region || 'CN'}</div>
    </div>
    <div class="p-4 border rounded-lg bg-card">
      <div class="text-sm font-medium text-muted-foreground">{t.created_at || '创建时间'}</div>
      <div class="text-sm font-semibold mt-3">
        {new Date(short_url.created_at).toLocaleString()}
      </div>
    </div>
  </div>

  <!-- 统计图表数据展示 -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    <!-- 浏览器分布 -->
    <div class="border rounded-lg p-4 space-y-3">
      <h3 class="font-semibold border-b pb-2">{t.browser_dist || '浏览器分布 (Top 5)'}</h3>
      <div class="space-y-2">
        {#each Object.entries(stats.browsers || {}) as [browser, count]}
          <div class="flex justify-between text-sm">
            <span>{browser || 'Unknown'}</span>
            <span class="font-mono font-bold">{count}</span>
          </div>
        {:else}
          <p class="text-sm text-muted-foreground">{t.no_data || '暂无数据'}</p>
        {/each}
      </div>
    </div>

    <!-- 操作系统分布 -->
    <div class="border rounded-lg p-4 space-y-3">
      <h3 class="font-semibold border-b pb-2">{t.os_dist || '操作系统分布 (Top 5)'}</h3>
      <div class="space-y-2">
        {#each Object.entries(stats.os || {}) as [os_name, count]}
          <div class="flex justify-between text-sm">
            <span>{os_name || 'Unknown'}</span>
            <span class="font-mono font-bold">{count}</span>
          </div>
        {:else}
          <p class="text-sm text-muted-foreground">{t.no_data || '暂无数据'}</p>
        {/each}
      </div>
    </div>

    <!-- 国家/地区分布 -->
    <div class="border rounded-lg p-4 space-y-3">
      <h3 class="font-semibold border-b pb-2">{t.country_dist || '国家/地区 (Top 10)'}</h3>
      <div class="space-y-2">
        {#each Object.entries(stats.countries || {}) as [country, count]}
          <div class="flex justify-between text-sm">
            <span>{country || 'Unknown'}</span>
            <span class="font-mono font-bold">{count}</span>
          </div>
        {:else}
          <p class="text-sm text-muted-foreground">{t.no_data || '暂无数据'}</p>
        {/each}
      </div>
    </div>

    <!-- 城市分布 -->
    <div class="border rounded-lg p-4 space-y-3">
      <h3 class="font-semibold border-b pb-2">{t.city_dist || '主要城市 (Top 10)'}</h3>
      <div class="space-y-2">
        {#each Object.entries(stats.cities || {}) as [city, count]}
          <div class="flex justify-between text-sm">
            <span>{city || 'Unknown'}</span>
            <span class="font-mono font-bold">{count}</span>
          </div>
        {:else}
          <p class="text-sm text-muted-foreground">{t.no_data || '暂无数据'}</p>
        {/each}
      </div>
    </div>
  </div>

  <!-- 最近访问明细明细 -->
  <div class="border rounded-lg p-4 space-y-4">
    <h3 class="font-semibold">{t.recent_visits || '最近 20 次访问记录'}</h3>
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.Head>{t.ip_address || 'IP 地址'}</Table.Head>
          <Table.Head>{t.browser || '浏览器'}</Table.Head>
          <Table.Head>{t.os || '操作系统'}</Table.Head>
          <Table.Head>{t.country || '国家/地区'}</Table.Head>
          <Table.Head>{t.city || '城市'}</Table.Head>
          <Table.Head>{t.time || '时间'}</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {#each stats.recent_visits || [] as visit (visit.id)}
          <Table.Row>
            <Table.Cell class="font-mono">{visit.ip_address || '-'}</Table.Cell>
            <Table.Cell>{visit.browser || '-'}</Table.Cell>
            <Table.Cell>{visit.os || '-'}</Table.Cell>
            <Table.Cell>{visit.country || '-'}</Table.Cell>
            <Table.Cell>{visit.city || '-'}</Table.Cell>
            <Table.Cell class="text-xs text-muted-foreground">
              {new Date(visit.created_at).toLocaleString()}
            </Table.Cell>
          </Table.Row>
        {:else}
          <Table.Row>
            <Table.Cell colspan={6} class="text-center text-muted-foreground py-4">
              {t.no_visits || '暂无访问明细记录'}
            </Table.Cell>
          </Table.Row>
        {/each}
      </Table.Body>
    </Table.Root>
  </div>
</div>